import { forwardRef } from 'react';
import { Box, Card, Paragraph } from '@zillow/constellation-10';
import Notepad from '../../Notepad';
import useLeadRestrictions from '../../../hooks/useLeadRestrictions';
import { useCachedLeadByConversationId } from '../../../hooks/useLeadManagementTableQuery';
import useManualTourSchedulingEnabled from '../../../hooks/useManualTourSchedulingEnabled';

type RenterDetailsNotepadProps = {
  clientTimeZone: string;
  conversationId: string;
  leadId?: string | null;
  listingAlias: string;
  shouldRefetchStatusLabels: boolean;
  resetAllInlineFeedbackChildState?: boolean;
  onResetAllInlineFeedbackChildState?: () => void;
  shouldSaveNoteText?: boolean;
  onHandleNoteSavingComplete?: () => void;
  isBackgroundHighlighted: boolean;
};

const RenterDetailsNotepad = forwardRef<HTMLDivElement, RenterDetailsNotepadProps>(
  (
    {
      clientTimeZone,
      conversationId,
      leadId,
      listingAlias,
      shouldRefetchStatusLabels,
      resetAllInlineFeedbackChildState = false,
      onResetAllInlineFeedbackChildState = () => {},
      shouldSaveNoteText = false,
      onHandleNoteSavingComplete = () => {},
      isBackgroundHighlighted,
    },
    ref,
  ): JSX.Element | null => {
    const isManualTourSchedulingEnabled = useManualTourSchedulingEnabled();
    const lead = useCachedLeadByConversationId(conversationId);
    const { canScheduleTour } = useLeadRestrictions(lead);

    return (
      <>
        <Box
          ref={ref}
          css={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: 'loose',
          }}
        >
          <Paragraph textStyle="body-sm-bold">Your notepad</Paragraph>
          <Paragraph textStyle="body-xs">Your updates will be automatically saved</Paragraph>
        </Box>
        <Card
          elevated={false}
          tone="neutral"
          css={{
            backgroundColor: isBackgroundHighlighted ? 'blue.100' : 'white',
            marginTop: 'default',
            padding: 'loose',
          }}
        >
          <Notepad
            canAddTour={isManualTourSchedulingEnabled && canScheduleTour}
            clientTimeZone={clientTimeZone}
            conversationId={conversationId}
            leadId={leadId}
            listingAlias={listingAlias}
            shouldRefetchStatusLabels={shouldRefetchStatusLabels}
            resetAllInlineFeedbackChildState={resetAllInlineFeedbackChildState}
            onResetAllInlineFeedbackChildState={onResetAllInlineFeedbackChildState}
            shouldSaveNoteText={shouldSaveNoteText}
            onHandleNoteSavingComplete={onHandleNoteSavingComplete}
          />
        </Card>
      </>
    );
  },
);

RenterDetailsNotepad.displayName = 'RenterDetailsNotepad';
export default RenterDetailsNotepad;
